import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router';
import { useQuery } from 'react-query';
import { getAssessment } from 'src/api/assessment';
import { ProjectionVariant, QKeys } from 'src/api/types';
import Meta from 'src/components/Meta';
import { PageContainer, PageChartSkeleton, Spacer, PageCard, VariantValues, Divider, Poppins } from 'src/common';
import { useGaEventPageView } from '../../utils/gaEvent';
import { Head } from './comps';
import useVersion from 'src/hooks/useVersion';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import { AuditSection, ExceedanceSection, RiskRegisterSection } from './sections';
import { OverviewSection } from './sections/OverviewSection';
import { ObjectKind, useAssessmentQuery } from 'src/graphql/_generated';
import { isQueryLoading } from 'src/graphql';
import ActionsSection from 'src/components/ActionsSection';
import { LABELS } from 'src/components/labels';

const Card = styled(PageCard)`
  padding-bottom: 0;
`;

const AssessmentPage = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const [isLogarithmic, setIsLogarithmic] = useState(true);
  const { version } = useVersion();
  const { setLocationPaths } = useActionsApp();

  const {
    data: { assessment } = {},
    error,
    loading,
    networkStatus,
  } = useAssessmentQuery({ variables: { id: assessmentId!, versionId: version } });

  const isLoading = isQueryLoading({ data: !!assessment, loading, networkStatus });

  const { data } = useQuery(
    [QKeys.Assessment, { assessmentId, version, variant: ProjectionVariant.live }],
    getAssessment,
  );

  const showAllProjected = !!assessment?.scenarios?.flatMap((el) =>
    el.riskManagementPlans
      ?.filter((rmp) => rmp.control && rmp.control.status !== 'Abandoned' && rmp.control.status !== 'Retired')
      ?.flatMap((rmp) => rmp.modifiers)
      ?.filter((mod) => mod.type !== 'NOTE_TYPE'),
  )?.length;

  useEffect(() => {
    if (assessment) {
      setLocationPaths({ assessment: { id: assessment.id, name: assessment.name } });
    }
    return () => {
      setLocationPaths({ assessment: undefined });
    };
  }, [assessment]);

  useGaEventPageView('Cydea | Assessment');

  if (isLoading)
    return (
      <PageContainer>
        <PageChartSkeleton />
      </PageContainer>
    );

  return (
    <>
      <Meta title={`Cydea | ${assessment?.name}`} feedbackScript />
      <PageContainer>
        {assessment && (
          <Card>
            <Head
              data={assessment}
              isLogarithmic={isLogarithmic}
              expectedLossComponent={
                <VariantValues
                  flat
                  value={
                    showAllProjected
                      ? {
                          base: assessment?.AssessmentBaseRisk?.expectedLoss,
                          live: assessment?.AssessmentLiveRisk?.expectedLoss,
                          cpmplete: assessment?.AssessmentProjectedRisk?.expectedLoss,
                        }
                      : assessment?.AssessmentBaseRisk?.expectedLoss || 0
                  }
                  tooltipText={{
                    base: LABELS.riskBase,
                    live: LABELS.riskLive,
                    cpmplete: LABELS.riskComplete,
                  }}
                />
              }
            />
            <Spacer $px={20} />
            <Divider />
            <ExceedanceSection
              data={assessment}
              version={version}
              showAllProjected={showAllProjected}
              isLogarithmic={isLogarithmic}
              setIsLogarithmic={setIsLogarithmic}
            />
            <Divider />
            <OverviewSection data={assessment} />
            <Divider />
            <RiskRegisterSection version={version} showAllProjected={showAllProjected} data={assessment} />
            <Divider />
            <ActionsSection objectType={ObjectKind.Assessment} objectId={assessment.id} name={assessment.name} />
            <Divider />
            <AuditSection version={version} />
          </Card>
        )}
        {error && <Poppins color="error">{error.message}</Poppins>}
      </PageContainer>
    </>
  );
};

export default AssessmentPage;
